import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

// Notification type
interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
  read: boolean;
}

// Socket event data types
interface AlertData {
  message: string;
}

interface SocialPublishData {
  platform: string;
  error?: string;
}

interface BidChangeData {
  keyword: string;
  oldBid: number;
  newBid: number;
}

interface AutomationData {
  name: string;
  error?: string;
}

interface AuditData {
  status: string;
  scores?: {
    overall: number;
  };
}

interface InsightData {
  message: string;
}

// Socket type placeholder (will be replaced when socket.io-client is installed)
type SocketType = {
  on: (event: string, callback: (...args: unknown[]) => void) => void;
  emit: (event: string, data?: unknown) => void;
  disconnect: () => void;
} | null;

// Socket context type
interface SocketContextType {
  socket: SocketType;
  isConnected: boolean;
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [socket, setSocket] = useState<SocketType>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Connect to socket when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    // Dynamic import for socket.io-client
    const connectSocket = async () => {
      try {
        const { io } = await import('socket.io-client');

        const newSocket = io(SOCKET_URL, {
          auth: { token },
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          transports: ['websocket', 'polling']
        });

        newSocket.on('connect', () => {
          setIsConnected(true);
          console.log('Socket connected');
        });

        newSocket.on('disconnect', (reason: string) => {
          setIsConnected(false);
          console.log('Socket disconnected:', reason);
        });

        newSocket.on('auth:success', () => {
          console.log('Socket authenticated');
        });

        newSocket.on('auth:error', (error: unknown) => {
          console.error('Socket auth error:', error);
          toast.error(t('common.connectionError'));
        });

        // Handle notifications
        newSocket.on('notification', (notification: Omit<Notification, 'id' | 'read'>) => {
          // If the server sent translation keys + params, render them in the user's
          // current UI language; otherwise fall back to the server-provided text.
          const i18nInfo = (notification as any).i18n;
          const title: string = i18nInfo?.titleKey
            ? (t(i18nInfo.titleKey, { defaultValue: notification.title, ...(i18nInfo.params || {}) }) as string)
            : notification.title;
          const message: string = i18nInfo?.messageKey
            ? (t(i18nInfo.messageKey, { defaultValue: notification.message, ...(i18nInfo.params || {}) }) as string)
            : notification.message;

          const newNotification: Notification = {
            ...notification,
            title,
            message,
            id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            read: false
          };

          setNotifications(prev => [newNotification, ...prev].slice(0, 100));

          toast(message, {
            icon: getNotificationIcon(notification.type),
            duration: 5000
          });
        });

        // Handle alerts
        newSocket.on('alert', (alert: AlertData) => {
          toast(alert.message, {
            icon: '⚠️',
            duration: 6000
          });
        });

        // Handle critical alerts
        newSocket.on('alert:critical', (alert: AlertData) => {
          toast.error(alert.message, {
            duration: 10000
          });
        });

        // Handle social publish success
        newSocket.on('social:publish:success', (data: SocialPublishData) => {
          toast.success(t('social.publishSuccess', { platform: data.platform }), {
            icon: '📱'
          });
        });

        // Handle social publish error
        newSocket.on('social:publish:error', (data: SocialPublishData) => {
          toast.error(t('social.publishError', { platform: data.platform, error: data.error }));
        });

        // Handle bid changes
        newSocket.on('bid:change', (data: BidChangeData) => {
          toast(t('keywords.bidChanged', { keyword: data.keyword, oldBid: data.oldBid, newBid: data.newBid }), {
            icon: '💰',
            duration: 5000
          });
        });

        // Handle automation triggered
        newSocket.on('automation:triggered', (data: AutomationData) => {
          toast(t('automation.triggered', { name: data.name }), {
            icon: '⚡'
          });
        });

        // Handle automation completed
        newSocket.on('automation:completed', (data: AutomationData) => {
          toast.success(t('automation.completed', { name: data.name }), {
            icon: '✅'
          });
        });

        // Handle automation error
        newSocket.on('automation:error', (data: AutomationData) => {
          toast.error(t('automation.error', { message: data.error }));
        });

        // Handle site audit progress
        newSocket.on('siteaudit:progress', (_data: unknown) => {
          // This can be handled by specific components
        });

        // Handle site audit complete
        newSocket.on('siteaudit:complete', (data: AuditData) => {
          if (data.status !== 'failed') {
            toast.success(t('competitors.auditComplete', { score: data.scores?.overall }), {
              icon: '📊'
            });
          } else {
            toast.error(t('competitors.auditFailed'));
          }
        });

        // Handle AI insights
        newSocket.on('ai:insight', (insight: InsightData) => {
          toast(insight.message, {
            icon: '🤖',
            duration: 8000
          });
        });

        setSocket(newSocket as unknown as SocketType);

        return () => {
          newSocket.disconnect();
        };
      } catch (error) {
        console.warn('Socket.io-client not available:', error);
      }
    };

    connectSocket();
  }, [isAuthenticated, t]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );

    // Emit to server
    socket?.emit('notification:read', { notificationId });
  }, [socket]);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    socket?.emit('notification:read:all');
  }, [socket]);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        clearNotifications
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

// Helper function to get notification icon based on type
const getNotificationIcon = (type: string): string => {
  switch (type) {
    case 'campaign':
    case 'campaign_update':
      return '📈';
    case 'keyword':
    case 'bid_change':
      return '🔑';
    case 'social':
    case 'social_publish':
      return '📱';
    case 'automation':
      return '⚡';
    case 'site_audit':
      return '🔍';
    case 'competitor':
      return '🎯';
    case 'ai':
    case 'insight':
      return '🤖';
    case 'alert':
      return '⚠️';
    case 'success':
      return '✅';
    case 'error':
      return '❌';
    default:
      return '🔔';
  }
};

export default SocketContext;
